// Pure helpers for the Spending limits tab on the org Usage page and the
// read-only limit line on a Boo's People page (boo-backend docs/spend-limits.md §8).

import type {
  PersonRow,
  SpendCell,
  SpendDefaultRow,
  SpendDirectoryEntry,
  SpendLimitsPage,
  SpendPeopleLine,
  SpendPersonRef,
  SpendRow,
  SpendTarget,
} from "./api.ts";

export type Tone = "" | "warn" | "crit";
export type Period = "daily" | "monthly";

/** How a period reads after an amount: "$5 extra today". */
export const PERIOD_WORD: Record<Period, string> = { daily: "today", monthly: "this month" };

const usd2 = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const usd0 = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 });

/** Spend: always cents ("$8.04", "$0.00"). */
export function fmtSpent(n: number): string {
  return usd2.format(n);
}

/** A cap: whole dollars when whole ("$15"), cents otherwise ("$12.50"). */
export function fmtCap(n: number): string {
  return Number.isInteger(n) ? usd0.format(n) : usd2.format(n);
}

export function effectiveCap(cell: Pick<SpendCell, "capUsd" | "extraUsd">): number | null {
  return cell.capUsd == null ? null : cell.capUsd + cell.extraUsd;
}

/** Amber at the warning level, red at the limit. */
export function toneOf(spent: number, cap: number, warnPercent: number): Tone {
  if (cap <= 0) return "crit";
  const pct = (spent / cap) * 100;
  if (pct >= 100) return "crit";
  if (pct >= warnPercent) return "warn";
  return "";
}

export function targetKey(t: SpendTarget): string {
  switch (t.scope) {
    case "org":
      return "org";
    case "boo":
      return `boo:${t.containerId}`;
    case "group":
      return `group:${t.groupId}`;
    case "person":
      return `person:${t.personKey}`;
    case "person_default":
      return "person_default";
  }
}

/** The person key a People-page row joins on (§8.2). */
export function personKeyOf(person: Pick<PersonRow, "kind" | "personId" | "handles">): string | null {
  if (person.kind === "managed") return `u:${person.personId}`;
  const h = person.handles[0];
  if (!h) return null;
  return `g:${[h.provider, h.workspace, h.providerUserId].map(encodeURIComponent).join(":")}`;
}

/** The fuller period of a person's daily and monthly limits; null when neither is set. */
export function pickLine(
  daily: Pick<SpendCell, "capUsd" | "extraUsd" | "spentUsd">,
  monthly: Pick<SpendCell, "capUsd" | "extraUsd" | "spentUsd">,
  source: SpendPeopleLine["source"],
): SpendPeopleLine | null {
  const ratio = (c: typeof daily) => (c.capUsd == null ? -1 : c.spentUsd / Math.max(c.capUsd + c.extraUsd, 0.000001));
  const d = ratio(daily);
  const m = ratio(monthly);
  if (d < 0 && m < 0) return null;
  const [period, c] = d >= m ? (["daily", daily] as const) : (["monthly", monthly] as const);
  return { period, capUsd: c.capUsd!, extraUsd: c.extraUsd, spentUsd: c.spentUsd, source };
}

/** Whole dollars when whole, cents otherwise: "$6", "$8.40". */
function fmtCompact(n: number): string {
  return fmtCap(Math.round(n * 100) / 100);
}

export function peopleLineText(line: SpendPeopleLine): string {
  return `Spending limit: ${fmtCompact(line.spentUsd)} of ${fmtCompact(line.capUsd + line.extraUsd)} ${PERIOD_WORD[line.period]}`;
}

/** The People-page line carries no warning level, so it uses the 80% default. */
export function peopleLineTone(line: SpendPeopleLine): Tone {
  return toneOf(line.spentUsd, line.capUsd + line.extraUsd, 80);
}

function joinAnd(items: string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

export const ROLE_WORD: Record<NonNullable<SpendPersonRef["role"]>, string> = {
  owner: "owner",
  admin: "admin",
  billing_admin: "billing admin",
};

/** "Jordan Lee (owner) and Priya Shah (billing admin)". */
export function namesWithRoles(people: SpendPersonRef[]): string {
  return joinAnd(people.map((p) => (p.role ? `${p.name} (${ROLE_WORD[p.role]})` : p.name)));
}

/** "Pacific Time" for America/Los_Angeles, "UTC" for UTC. */
export function timezoneLabel(tz: string): string {
  if (tz === "UTC" || tz === "Etc/UTC") return "UTC";
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "longGeneric" })
      .formatToParts(new Date())
      .find((p) => p.type === "timeZoneName");
    return part?.value ?? tz.replace(/_/g, " ");
  } catch {
    return tz.replace(/_/g, " ");
  }
}

/** "November 1": the local date a monthly period resets on. */
export function monthResetLabel(resetsAt: string, tz: string): string {
  const d = new Date(resetsAt);
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone: tz, month: "long", day: "numeric" }).format(d);
  } catch {
    return new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric" }).format(d);
  }
}

// ── Editing ────────────────────────────────────────────────────────

export type Parsed = { v: number | null; bad?: false } | { bad: true };

/** Blank = no limit. "$1,200" is accepted. Zero or negative is invalid. */
export function parseAmount(s: string): Parsed {
  const t = s.trim().replace(/[$,]/g, "");
  if (t === "") return { v: null };
  const n = Math.round(Number(t) * 100) / 100;
  return Number.isFinite(n) && n > 0 ? { v: n } : { bad: true };
}

export type Draft = { daily: string; monthly: string; warn: string };
export type HintKind = "err" | "impact" | "info";
export type Hint = [HintKind, string];

/** What the draft check needs to know about the row being edited. */
export type DraftContext = {
  /** How the row is named in a sentence: "Lumen", "Pip", "Marketing", "Sam". */
  who: string;
  /** Who a stop applies to: "every Boo", "it", "everyone in the group", "them". */
  object: string;
  dailySpent: number;
  monthlySpent: number;
  /** This period's one-time extras, which raise whatever cap is saved. */
  dailyExtra: number;
  monthlyExtra: number;
  /** "November 1" */
  monthReset: string;
  /** Set only on the "Everyone (default)" row. */
  defaultRow?: { appliesTo: number };
};

export type DraftResult = {
  ok: boolean;
  hints: Hint[];
  daily: number | null;
  monthly: number | null;
  warn: number;
  dailyBad: boolean;
  monthlyBad: boolean;
  warnBad: boolean;
};

export function checkDraft(draft: Draft, ctx: DraftContext): DraftResult {
  const hints: Hint[] = [];
  const dd = parseAmount(draft.daily);
  const mm = parseAmount(draft.monthly);
  const warnText = draft.warn.trim().replace(/%$/, "");
  const w = Number(warnText);
  let ok = true;
  if (dd.bad || mm.bad) {
    hints.push(["err", "Enter an amount above $0, or leave it blank for no limit."]);
    ok = false;
  }
  const daily = dd.bad ? null : dd.v;
  const monthly = mm.bad ? null : mm.v;
  if (daily != null && monthly != null && daily > monthly) {
    hints.push(["err", "The daily limit can't be higher than the monthly limit."]);
    ok = false;
  }
  const warnBad = !(warnText !== "" && Number.isInteger(w) && w >= 1 && w <= 100);
  if (warnBad) {
    hints.push(["err", "Pick a warning level between 1% and 100%."]);
    ok = false;
  }
  if (ok && !ctx.defaultRow) {
    if (daily != null && daily + ctx.dailyExtra <= ctx.dailySpent) {
      hints.push(["impact", `${ctx.who} has used ${fmtSpent(ctx.dailySpent)} today, so this stops ${ctx.object} until midnight.`]);
    }
    if (monthly != null && monthly + ctx.monthlyExtra <= ctx.monthlySpent) {
      hints.push(["impact", `${ctx.who} has used ${fmtSpent(ctx.monthlySpent)} this month, so this stops ${ctx.object} until ${ctx.monthReset}.`]);
    }
  }
  if (ok && ctx.defaultRow) {
    const n = ctx.defaultRow.appliesTo;
    hints.push(["info", `Changes it for ${n} ${n === 1 ? "person" : "people"}.`]);
  }
  return { ok, hints, daily, monthly, warn: w, dailyBad: !!dd.bad, monthlyBad: !!mm.bad, warnBad };
}

/** A one-time extra: a positive amount. */
export function parseExtra(s: string): number | null {
  const p = parseAmount(s);
  return p.bad || p.v == null ? null : p.v;
}

/** The warn % to send: omitted while the row stays on the org default. */
export function warnToSend(row: Pick<SpendRow, "warnPercent" | "warnPercentOwn">, warn: number): number | undefined {
  if (row.warnPercentOwn != null) return warn;
  return warn === row.warnPercent ? undefined : warn;
}

export function draftOf(row: Pick<SpendRow, "daily" | "monthly" | "warnPercent">): Draft {
  return {
    daily: row.daily.capUsd == null ? "" : String(row.daily.capUsd),
    monthly: row.monthly.capUsd == null ? "" : String(row.monthly.capUsd),
    warn: String(row.warnPercent),
  };
}

export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

/** Whether the row caps either period. A row without one never warns. */
export function hasCap(row: Pick<SpendRow, "daily" | "monthly">): boolean {
  return row.daily.capUsd != null || row.monthly.capUsd != null;
}

/** "$10 a day, no monthly limit"; null when the row caps neither period. */
export function capsSummary(row: Pick<SpendRow, "daily" | "monthly">): string | null {
  if (!hasCap(row)) return null;
  const d = row.daily.capUsd;
  const m = row.monthly.capUsd;
  return `${d == null ? "no daily limit" : `${fmtCap(d)} a day`}, ${m == null ? "no monthly limit" : `${fmtCap(m)} a month`}`;
}

/**
 * The "Everyone (default)" row. Editors always get one, so a default can be
 * set when none exists; a backend that returns null for an unset default
 * gets a row with no limits in its place.
 */
export function defaultRowOf(page: SpendLimitsPage): SpendDefaultRow | null {
  if (page.people.default || !page.viewer.canEdit) return page.people.default;
  const any = page.org ?? page.boos[0] ?? page.groups[0] ?? page.people.overrides[0];
  const resets = { daily: any?.daily.resetsAt ?? new Date().toISOString(), monthly: any?.monthly.resetsAt ?? new Date().toISOString() };
  const own = new Set(page.people.overrides.map((o) => o.person.personKey));
  return {
    target: { scope: "person_default" },
    name: "Everyone (default)",
    daily: { capUsd: null, extraUsd: 0, spentUsd: 0, resetsAt: resets.daily },
    monthly: { capUsd: null, extraUsd: 0, spentUsd: 0, resetsAt: resets.monthly },
    warnPercent: page.defaultWarnPercent,
    warnPercentOwn: null,
    extras: [],
    appliesTo: (page.directory ?? []).filter((p) => !own.has(p.personKey)).length,
  };
}

/** The chat app names in the backend's guest labels ("Guest · SMS"), SMS and iMessage included. */
const PROVIDER_LABEL: Record<string, string> = {
  slack: "Slack",
  teams: "Teams",
  gchat: "Google Chat",
  discord: "Discord",
  sendblue: "SMS",
  sms: "SMS",
  imessage: "iMessage",
  dashboard: "Web",
  dashboard_chat: "Web",
};

function parseGuest(personKey: string): { provider: string; tail: string } | null {
  if (!personKey.startsWith("g:")) return null;
  const parts = personKey.slice(2).split(":");
  if (parts.length !== 3) return null;
  try {
    const provider = decodeURIComponent(parts[0]);
    const id = decodeURIComponent(parts[2]);
    if (!provider || !id) return null;
    return { provider: PROVIDER_LABEL[provider] ?? provider, tail: id.replace(/[^A-Za-z0-9]/g, "").slice(-4) };
  } catch {
    return null;
  }
}

/**
 * The second line that tells guests apart. A guest named only by their chat
 * app ("Guest · Slack") gets the end of their account id from the guest key
 * ("ID ending 4F2K"); a named guest gets "Guest · Slack". Null for members.
 */
export function guestDetail(p: Pick<SpendPersonRef, "personKey" | "name">): string | null {
  const g = parseGuest(p.personKey);
  if (!g) return null;
  if (p.name.startsWith("Guest")) return g.tail ? `ID ending ${g.tail}` : null;
  return `Guest · ${g.provider}`;
}

/** Everything the person picker searches on: name, email, chat app and id ending. */
export function personSearchText(p: Pick<SpendDirectoryEntry, "personKey" | "name" | "email" | "isGuest">): string {
  const g = parseGuest(p.personKey);
  return [p.name, p.email, p.isGuest || g ? "guest" : "", g?.provider, g?.tail].filter(Boolean).join(" ");
}

/** "Warn at" in a sentence, for the edit and add rows. */
export function warnHint(draft: Draft, result: DraftResult): Hint | null {
  if (result.warnBad) return null;
  const limited = draft.daily.trim() !== "" || draft.monthly.trim() !== "";
  return ["info", limited
    ? `Warn at: a one-time heads-up when spending reaches ${result.warn}% of a limit.`
    : "Warn at: a one-time heads-up at that share of a limit, once there is one."];
}

/**
 * The person picker's filter: every word of the search appears in the
 * person's search text. The person key is never searched, so a short query
 * doesn't match random characters in member ids.
 */
export function personMatches(searchText: string, search: string): number {
  const text = searchText.toLowerCase();
  return search.toLowerCase().split(/\s+/).filter(Boolean).every((w) => text.includes(w)) ? 1 : 0;
}

/** "Ledger at 90%, Marketing at 60% and 2 more keep their own."; null when none do. */
export function keptOwnText(own: { name: string; warnPercent: number }[], max = 3): string | null {
  if (!own.length) return null;
  const shown = own.slice(0, max).map((l) => `${l.name} at ${l.warnPercent}%`);
  const more = own.length - shown.length;
  const list = more ? `${shown.join(", ")} and ${more} more` : shown.length > 1 ? `${shown.slice(0, -1).join(", ")} and ${shown.at(-1)}` : shown[0];
  return `${list} ${own.length === 1 ? "keeps its" : "keep their"} own.`;
}
